import { extractEvents, type ExtractedEvents } from '../av/events';
import { sessionReactions, sessionTiming } from '../av/sessionController';
import type { SessionTiming } from '../av/sessionClock';
import type { ReactionFrame } from '../av/reactions';
import { ProjectSchema, type Animation, type AvSession, type Project, type Scene } from '../domain/schemas';

/**
 * The frozen input of one video export (Phase 4B).
 *
 * Invariant: nothing that happens to the live project after Start can change an export,
 * and nothing in the export can change the project. Enforced, not assumed:
 *  - the project document is deep-copied (`structuredClone`) when the export starts, so
 *    edits through any path (the store, prompts, undo, or an in-place mutation that
 *    bypasses React) never reach the copy;
 *  - the copy is deep-frozen, so a renderer or audio step that tried to write to project
 *    data would throw instead of silently altering what is being exported;
 *  - the copy is validated with the persistence schema.
 * The document holds no binary data (image and audio bytes live in the blob store, keyed
 * by immutable asset ids), so the copy is small and bounded by the schema's limits; no
 * asset is duplicated. Asset bytes are captured separately: every image is decoded once
 * before the first frame (`SessionFrameRenderer.create`), and every decoded audio buffer
 * is resolved and held when audio rendering starts (`renderPlan`), so deleting an asset
 * later in the export cannot change it. An asset deleted before it was captured fails the
 * export with a message naming it.
 */
export interface ExportSnapshot {
  project: Project;
  session: AvSession;
  scene: Scene;
  animation: Animation | null;
  timing: SessionTiming;
  events: ExtractedEvents;
}

export class SnapshotError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SnapshotError';
  }
}

/** Validates the project (the same schema as persistence) and the session's references. */
export function takeSnapshot(live: Project, sessionId: string): ExportSnapshot {
  if (!live.avSessions.some((s) => s.id === sessionId)) throw new SnapshotError('The sync session no longer exists.');
  const project = deepFreeze(structuredClone(live));
  const session = project.avSessions.find((s) => s.id === sessionId);
  if (!session) throw new SnapshotError('The sync session no longer exists.');
  const parsed = ProjectSchema.safeParse(project);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new SnapshotError(`The project is not valid for export (${issue?.path.join('.') || 'project'}: ${issue?.message ?? 'invalid'}).`);
  }
  const scene = project.scenes.find((s) => s.id === session.sceneId);
  if (!scene) throw new SnapshotError('The session’s scene no longer exists.');
  let animation: Animation | null = null;
  if (session.animationId) {
    animation = project.animations.find((a) => a.id === session.animationId) ?? null;
    if (!animation) throw new SnapshotError('The session’s animation no longer exists.');
    if (animation.sceneId !== scene.id) throw new SnapshotError(`The animation “${animation.name}” belongs to another scene.`);
  }
  const imageIds = imageAssets(scene);
  for (const id of imageIds) {
    if (!project.assets.some((a) => a.id === id && a.kind === 'image')) throw new SnapshotError('An image used by the scene is missing from the project.');
  }
  const timing = sessionTiming(project, session);
  if (!(timing.durationSec > 0)) throw new SnapshotError('The session has no length to export.');
  return { project, session, scene, animation, timing, events: extractEvents(project, session) };
}

/**
 * The frozen input of an animation-only render (Phase 5C.1 visual review): a scene and
 * one of its animations, with no sync session. Same guarantees as `ExportSnapshot`
 * (deep-copied, frozen, validated). The timing is the animation's own: frame 0 at 0 s,
 * `durationFrames / fps` seconds long, its own loop rule; the clock maps seconds to frames
 * with `frameAtTime`, exactly as the Animation workspace's playback clock does. There are
 * no reactions, visualizer or audio: nothing is derived from music.
 */
export interface AnimationSnapshot {
  project: Project;
  scene: Scene;
  animation: Animation;
  timing: SessionTiming;
}

export function takeAnimationSnapshot(live: Project, animationId: string): AnimationSnapshot {
  if (!live.animations.some((a) => a.id === animationId)) throw new SnapshotError('The animation no longer exists.');
  const project = deepFreeze(structuredClone(live));
  const parsed = ProjectSchema.safeParse(project);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new SnapshotError(`The project is not valid for rendering (${issue?.path.join('.') || 'project'}: ${issue?.message ?? 'invalid'}).`);
  }
  const animation = project.animations.find((a) => a.id === animationId)!;
  const scene = project.scenes.find((s) => s.id === animation.sceneId);
  if (!scene) throw new SnapshotError('The animation’s scene no longer exists.');
  for (const id of imageAssets(scene)) {
    if (!project.assets.some((a) => a.id === id && a.kind === 'image')) throw new SnapshotError('An image used by the scene is missing from the project.');
  }
  const durationSec = animation.durationFrames / animation.fps;
  const timing: SessionTiming = { durationSec, loop: null, animationStartSec: 0, animation: { fps: animation.fps, durationFrames: animation.durationFrames, loop: animation.loop } };
  return { project, scene, animation, timing };
}

/** Image asset ids a scene draws (objects and background). */
export function imageAssets(scene: Scene): string[] {
  const ids = new Set<string>();
  for (const o of scene.objects) if (o.type === 'image') ids.add(o.assetId);
  if (scene.backgroundImage) ids.add(scene.backgroundImage.assetId);
  return [...ids];
}

/** Reactions and cue overlays of the snapshot at a session position (the preview's function). */
export const snapshotReactions = (s: ExportSnapshot, seconds: number): ReactionFrame | null => sessionReactions(s.project, s.session, s.events, seconds);

/** Freezes an object graph of plain data (objects and arrays) in place and returns it. */
export function deepFreeze<T>(value: T): T {
  const stack: unknown[] = [value];
  while (stack.length) {
    const v = stack.pop();
    if (v === null || typeof v !== 'object' || Object.isFrozen(v)) continue;
    Object.freeze(v);
    for (const child of Object.values(v as Record<string, unknown>)) if (child !== null && typeof child === 'object') stack.push(child);
  }
  return value;
}
